// src/pages/PortfolioPage.js
import { useState, useEffect } from 'react';
import api from '../utils/api';
import { Card, CardHeader, Btn, Table, formatCurrency, formatPct, Badge, PageHeader, StatCard } from '../components/ui';
import { Briefcase, TrendingUp, TrendingDown, RefreshCw } from 'lucide-react';

export default function PortfolioPage() {
  const [portfolio, setPortfolio] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get('/trading/portfolio').then(r => { setPortfolio(r.data); setLoading(false); }).catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const totalInvested = portfolio.reduce((s, p) => s + parseFloat(p.total_investment || 0), 0);
  const currentValue = portfolio.reduce((s, p) => s + parseFloat(p.current_value || 0), 0);
  const unrealizedPnL = currentValue - totalInvested;
  const pnlPct = totalInvested > 0 ? (unrealizedPnL / totalInvested) * 100 : 0;

  return (
    <div>
      <PageHeader
        title="Portfolio Holdings"
        subtitle="Current open positions"
        action={<Btn variant="ghost" size="sm" onClick={load}><RefreshCw size={14} /> Refresh</Btn>}
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
        <StatCard label="Total Holdings" value={`${portfolio.length}`} subvalue="Open positions" icon={Briefcase} color="#8b5cf6" />
        <StatCard label="Total Invested" value={formatCurrency(totalInvested)} icon={TrendingUp} color="#3b82f6" />
        <StatCard label="Current Value" value={formatCurrency(currentValue)} icon={TrendingUp} color="#10b981" />
        <StatCard
          label="Unrealized P&L"
          value={formatCurrency(unrealizedPnL)}
          subvalue={formatPct(pnlPct)}
          icon={unrealizedPnL >= 0 ? TrendingUp : TrendingDown}
          color={unrealizedPnL >= 0 ? '#10b981' : '#ef4444'}
          trend={unrealizedPnL >= 0 ? 'up' : 'down'}
        />
      </div>

      <Card>
        <CardHeader title="Holdings" subtitle={`${portfolio.length} positions`} />
        {loading ? (
          <div style={{ padding: 60, textAlign: 'center', color: '#64748b' }}>Loading portfolio...</div>
        ) : (
          <Table
            headers={['Script', 'Exchange', 'Qty', 'Avg Buy Price', 'Current Price', 'Total Invested', 'Current Value', 'Unrealized P&L', 'P&L %']}
            rows={portfolio.map(p => {
              const pnl = parseFloat(p.current_value) - parseFloat(p.total_investment);
              const pnlPctItem = parseFloat(p.total_investment) > 0 ? (pnl / parseFloat(p.total_investment)) * 100 : 0;
              return [
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>{p.script_code}</div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>{p.script_name}</div>
                </div>,
                <Badge color={p.exchange_code === 'NSE' ? 'blue' : 'orange'}>{p.exchange_code}</Badge>,
                <strong>{p.total_quantity}</strong>,
                formatCurrency(p.avg_buy_price),
                p.current_price ? formatCurrency(p.current_price) : <span style={{ color: '#94a3b8' }}>—</span>,
                formatCurrency(p.total_investment),
                formatCurrency(p.current_value),
                <strong style={{ color: pnl >= 0 ? '#10b981' : '#ef4444' }}>{formatCurrency(pnl)}</strong>,
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: pnlPctItem >= 0 ? '#10b981' : '#ef4444', fontWeight: 600 }}>
                  {pnlPctItem >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                  {formatPct(pnlPctItem)}
                </div>
              ];
            })}
            emptyMessage="No open positions. Start by recording a purchase."
          />
        )}
      </Card>
    </div>
  );
}
